import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, withCtx as _withCtx } from "vue"

import { computed, onBeforeMount, ref } from 'vue'
import type { Raw } from 'vue'

import { QuestionnaireView } from '@/models/onboarding'
import type { PlanSelection20676Page } from '@/models/onboarding.types'
import type { BillingView, PlanView } from '@/models/billing'
import { createBilling } from '@/models/billing'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import { Mixpanel } from '@/analytics/mixpanel'
import type { InterestBook } from '@/helpers/billingSourceBook'
import {
  getKnownContentOriginSlug,
  loadInterestBook,
} from '@/helpers/billingSourceBook'
import { DEFAULT_INTEREST_BOOK } from '@/helpers/bookCoverStack'
import {
  setCachedBilling20676,
  setInterestBook20676,
  setResolvedTitle20676,
  setSelectedPlanId20676,
} from '@/models/onboarding.plan-card-capture-20676'

import Layout from '@/app/components/onboarding/Layout.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import BookCoverStack from '@/app/components/book/BookCoverStack.vue'
import PlanSelectionStep20676 from '@/app/components/onboarding/plan-card-capture/PlanSelectionStep20676.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'PlanSelection20676',
  props: {
    view: {}
  },
  setup(__props: any) {

const props = __props

const page = computed(() => {
  return props.view.currentPage as PlanSelection20676Page
})

const billing = ref<Raw<BillingView> | null>(null)
const selectedPlan = ref<Raw<PlanView> | null>(null)
const title = ref(page.value.title)
const book = ref(DEFAULT_INTEREST_BOOK)

/**
 * In case of failure, we send the error to Sentry and return `null` which allows
 * us to show the default copy and image without failing the whole page load.
 */
async function loadInterestBookOrLog(
  slug: string,
): Promise<InterestBook | null> {
  try {
    return await loadInterestBook(slug)
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag('experiment', 'exp_onboarding_plan_cc_capture_20676')
      scope.setExtra('slug', slug)
      Sentry.captureMessage(
        'exp_onboarding_plan_cc_capture_20676: content origin unresolvable',
      )
    })
    return null
  }
}

onBeforeMount(async () => {
  // Clear out any plan id left over from a previous, abandoned pass through
  // these two pages -- PaymentCapture20676 must never fall back to a stale
  // selection instead of the freshly-loaded annual default.
  setSelectedPlanId20676(null)

  const slug = getKnownContentOriginSlug()

  // Billing and the book are independent, so we run both requests together,
  // keeping the loading spinner up until both have settled.
  let response: Awaited<ReturnType<typeof backend.getBilling>>
  let interestBook: InterestBook | null
  try {
    ;[response, interestBook] = await Promise.all([
      backend.getBilling(),
      slug ? loadInterestBookOrLog(slug) : Promise.resolve(null),
    ])
  } catch (error) {
    Sentry.captureException(error)
    // If billing can't load, don't strand the user on a broken screen --
    // treat it like a skip.
    props.view.skipPlanCardCapture20676()
    return
  }

  if (interestBook) {
    book.value = interestBook
    title.value = `${interestBook.title} is waiting for you!`
  }

  const resolvedBilling = await createBilling(response)
  billing.value = resolvedBilling
  selectedPlan.value = resolvedBilling.annualPlan
  // Nothing between this page and PaymentCapture20676 can change billing
  // state (no subscribe happens until that page's own submit) -- bridge
  // it instead of making PaymentCapture20676 fetch and reconstruct it
  // again a page later.
  setCachedBilling20676(resolvedBilling)

  // PaymentCapture20676 is a separate page with no shared component state.
  // We save the resolved title (generic or personalized) so it doesn't fall
  // back to its own static page config placeholder.
  setResolvedTitle20676(title.value)

  // Same reasoning -- PaymentCapture20676 shows the same book cover stack,
  // so it needs the resolved (or default) book too.
  setInterestBook20676(book.value)
})

function onContinue(): void {
  setSelectedPlanId20676(selectedPlan.value?.id ?? null)
  props.view.goNextPage()
}

function onSkip(): void {
  Mixpanel.trackOnboardingBillingSkipClick(props.view.currentPage.id)
  props.view.skipPlanCardCapture20676()
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Layout, {
    title: "Start your 5-day free trial",
    subtitle: title.value,
    "reverse-title-subtitle": "",
    class: "plan-selection-20676"
  }, {
    default: _withCtx(() => [
      (!billing.value || !selectedPlan.value)
        ? (_openBlock(), _createBlock(LoadingSpinner, { key: 0 }))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createVNode(BookCoverStack, {
              book: book.value,
              class: "plan-selection-20676__book-stack"
            }, null, 8, ["book"]),
            _createVNode(PlanSelectionStep20676, {
              billing: billing.value,
              "selected-plan": selectedPlan.value,
              "onUpdate:selectedPlan": _cache[0] || (_cache[0] = ($event: any) => (selectedPlan.value = $event)),
              onContinue: onContinue,
              onSkip: onSkip
            }, null, 8, ["billing", "selected-plan"])
          ], 64))
    ]),
    _: 1
  }, 8, ["subtitle"]))
}
}

})